import {
  MACRO_CATALOG,
  type MacroTypeaheadEntry,
  type TypeaheadEntry,
  type VariableTypeaheadEntry,
} from '@chatsdet/types';
import type { SuggestionInsertResult, SuggestionItem } from '@chatsdet/ui';
import { createMacroSuggestionRenderer } from './MacroSuggestionPopover';

const MACRO_ENTRIES: MacroTypeaheadEntry[] = MACRO_CATALOG.map((entry) => ({
  ...entry,
  kind: 'macro',
}));

function entryToSuggestionItem(entry: TypeaheadEntry): SuggestionItem {
  const id =
    entry.kind === 'variable' && entry.qualifiedName
      ? `var:${entry.qualifiedName}`
      : `${entry.kind}:${entry.name}`;
  return {
    id,
    label: entry.kind === 'macro' ? `$${entry.name}()` : `$var.${entry.name}`,
    ...entry,
  };
}

export function getMacroSuggestionItems(
  query: string,
  variableEntries: readonly VariableTypeaheadEntry[],
  includeVariables: boolean
): SuggestionItem[] {
  const upper = query.toUpperCase();
  const lower = query.toLowerCase();

  let entries: readonly TypeaheadEntry[];
  if (includeVariables && lower.startsWith('var.')) {
    const variablePrefix = lower.slice(4);
    entries = variableEntries.filter((entry) => {
      const matchesName = entry.name.toLowerCase().startsWith(variablePrefix);
      const matchesQualified = entry.qualifiedName?.toLowerCase().startsWith(variablePrefix);
      return matchesName || matchesQualified;
    });
  } else if (includeVariables && 'var.'.startsWith(lower) && lower.length > 0) {
    entries = variableEntries;
  } else {
    const macros = MACRO_ENTRIES.filter((entry) => entry.name.startsWith(upper));
    const variables = includeVariables
      ? variableEntries.filter((entry) => entry.name.toLowerCase().startsWith(lower))
      : [];
    entries = [...macros, ...variables];
  }

  return entries.map(entryToSuggestionItem);
}

export function getMacroSuggestionInsertResult(item: SuggestionItem): SuggestionInsertResult {
  const entry = item as unknown as TypeaheadEntry;

  if (entry.kind === 'variable') {
    const text = entry.qualifiedName ? `$var.${entry.qualifiedName}` : `$var.${entry.name}`;
    return { text, tokenType: 'variable' };
  }

  if (entry.args.some((arg) => !arg.optional)) {
    return { plainText: `$${entry.name}()`, cursorOffset: 1 };
  }

  return { text: `$${entry.name}()`, tokenType: 'macro' };
}

export function createMacroSuggestionTrigger(
  variableEntries: readonly VariableTypeaheadEntry[],
  includeVariables: boolean
) {
  return {
    char: '$',
    allowSpaces: false,
    items: (query: string) => getMacroSuggestionItems(query, variableEntries, includeVariables),
    render: () => createMacroSuggestionRenderer(),
    onSelect: getMacroSuggestionInsertResult,
  };
}
